import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { login } from '../store/authSlice';
import { RootState, AppDispatch } from '../store';
import { FaEnvelope, FaLock, FaSpinner, FaEye, FaEyeSlash } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { trackEvent } from '../utils/analytics';
import SEOHead from '../components/SEOHead';
import {
  getDefaultAuthenticatedPath,
  getSafeAuthRedirect,
} from '../utils/authRedirect';

const LoginPage = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { loading, error, isAuthenticated, user } = useSelector((state: RootState) => state.auth);
  const requestedRedirect = searchParams.get('redirect');
  const forwardRedirect = getSafeAuthRedirect(requestedRedirect);
  const signUpParams = new URLSearchParams();
  if (forwardRedirect === '/post-gig') signUpParams.set('role', 'employer');
  if (forwardRedirect) signUpParams.set('redirect', forwardRedirect);
  const signUpQuery = signUpParams.toString();
  const signUpUrl = signUpQuery ? `/register?${signUpQuery}` : '/register';
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      const redirectPath = user?.role
        ? getSafeAuthRedirect(requestedRedirect, user.role)
        : '';
      navigate(
        redirectPath || getDefaultAuthenticatedPath(user?.role),
      );
    }
  }, [isAuthenticated, navigate, requestedRedirect, user?.role]);

  useEffect(() => {
    if (error) {
      toast.error(error);
    }
  }, [error]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const result = await dispatch(login(formData));
    if (login.fulfilled.match(result)) {
      trackEvent('login', 'engagement');
      toast.success('Welcome back!');
      const authenticatedRole = result.payload.user?.role;
      const redirectPath = authenticatedRole
        ? getSafeAuthRedirect(requestedRedirect, authenticatedRole)
        : '';
      navigate(
        redirectPath || getDefaultAuthenticatedPath(authenticatedRole),
      );
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4">
      <SEOHead
        title="Sign In — StageCrew | AV & Stagehand Job Marketplace"
        description="Sign in to your StageCrew account. Access your AV technician and stagehand gig applications, messages, and profile."
        url="https://stagecrew.app/login"
      />
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900">Welcome Back</h1>
          <p className="text-gray-600 mt-2">Sign in to your StageCrew account</p>
        </div>

        <div className="bg-white p-8 rounded shadow">
          <form onSubmit={handleSubmit} className="space-y-6" aria-busy={loading}>
            <div>
              <label htmlFor="login-email" className="block text-sm font-medium text-gray-700 mb-2">
                Email Address
              </label>
              <div className="relative">
                <FaEnvelope className="absolute left-3 top-3 text-gray-400" aria-hidden="true" />
                <input
                  id="login-email"
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  autoComplete="email"
                  placeholder="you@example.com"
                  className="min-h-11 w-full rounded border border-gray-500 py-2 pl-10 pr-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div>
              <label htmlFor="login-password" className="block text-sm font-medium text-gray-700 mb-2">
                Password
              </label>
              <div className="relative">
                <FaLock className="absolute left-3 top-3 text-gray-400" aria-hidden="true" />
                <input
                  id="login-password"
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  autoComplete="current-password"
                  placeholder="Enter your password"
                  className="min-h-11 w-full rounded border border-gray-500 py-2 pl-10 pr-11 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(prev => !prev)}
                  className="absolute inset-y-0 right-0 flex min-h-11 min-w-11 items-center justify-center rounded text-gray-500 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <FaEyeSlash aria-hidden="true" /> : <FaEye aria-hidden="true" />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              aria-busy={loading}
              className="w-full bg-blue-600 text-white py-3 rounded font-bold hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center"
            >
              {loading ? (
                <FaSpinner className="animate-spin mr-2" aria-hidden="true" />
              ) : null}
              {loading ? 'Signing In...' : 'Sign In'}
            </button>
          </form>
          <p className="sr-only" role="status" aria-live="polite" aria-atomic="true">
            {loading ? 'Signing in to StageCrew.' : ''}
          </p>

          <div className="mt-4 text-center">
            <Link to="/forgot-password" className="inline-flex min-h-11 items-center text-sm text-gray-500 hover:text-blue-600 hover:underline">
              Forgot your password?
            </Link>
          </div>

          <div className="mt-4 text-center">
            <p className="text-gray-600">
              Don't have an account?{' '}
              <Link to={signUpUrl} className="inline-flex min-h-11 items-center font-medium text-blue-600 hover:underline">
                Sign Up
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
